import { Injectable, OnDestroy } from '@angular/core';
import { GtmExternal, PageTrackingGtmDto, BaseGTMDTO, FavoriteGTMDto,
  ModalTrackingGtmDto, VisitNudgeDto, CartGTM, ECommerceItemArray, Item, ReciptTypeNames, OrderGTM} from '@shared/models/configs/gtm.external';
import { select } from '@angular-redux/store';
import { Observable, Subscription } from 'rxjs';
import { ChildRecipeEntity, LocationsEntity, Order, PickupType, PickupTypeDisplayValue, User, isModifierGroupDefaultSelected } from '@core/redux/stores';
import * as _ from 'lodash-es';
import { LocTrackingService } from './location-tracking.service';
import { LoggingService } from './logging.service';
import { OrderSummaryItems } from '@app/features/checkout/models';
/**
 * This service send the datalayer to the GTM container.
 * All the DataLayer.push will be in one place
 */
@Injectable()
export class GtmService implements OnDestroy {


  @select((appState) => appState.auth.isLoggedIn) isLoggedIn?: Observable<boolean>;
  @select((appState) => appState.user) userObject: Observable<User>;

  private isUserLoggedIn: boolean;
  private user: User;
  public GTM_APPLEPAY ='Apple Pay';
  public GTM_VISA='Visa';
  public GTM_MASTERCARD='Master';
  public GTM_PAYPAL='Paypal';
  public GTM_GIFT_CARD='Whataburger Gift Card';
  public GTM_AMEX='Amex';
  public GTM_DISOCVER='Discover';
  public GTM_GOOGLE_PAY='Google Pay';

  private subscriptions = new Subscription();
  private dataLayer: any;

  constructor(
    private locTrackingService: LocTrackingService,
    private logger: LoggingService
  ) {
    this.subscriptions.add(
    this.isLoggedIn.subscribe(
      (auth) => {
        this.isUserLoggedIn = auth ? auth : false;
      }
    ));
    this.subscriptions.add(
    this.userObject.subscribe(
      (resp) => {
        this.user = <User>_.cloneDeep(resp);
      }
    ));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setDataLayer(dataLayer: any) {
    this.dataLayer = dataLayer;
  }

  sendDataLayer(object: any) {
    this.dataLayer.push({
      object
    });
  }

  private getWindowsObject(): any {
    return window;
  }
   /**
    * All landing page will consume this service to log the page load event
    * @param pageTracking
    */
  private sendPageLoadEvent(pageTracking: PageTrackingGtmDto): void {
    if (pageTracking) {
      try {
        const localPageTracking = new PageTrackingGtmDto();
        localPageTracking.event = pageTracking.event ? pageTracking.event : GtmExternal.EVENT_CONTENTCHANGE;
        localPageTracking.pagePath = pageTracking.pagePath ? pageTracking.pagePath : this.getWindowsObject().location.pathname;
        localPageTracking.pageUrl =  pageTracking.pageUrl ? pageTracking.pageUrl :
            'https:' + this.getWindowsObject().location.href.substring(
          this.getWindowsObject().location.protocol.length);
        localPageTracking.pageName = pageTracking.pageName;
        localPageTracking.userId = (!this.isUserLoggedIn) ? null : this.user.id;
        localPageTracking.ecommerce = pageTracking.ecommerce ? pageTracking.ecommerce : null;
        localPageTracking.locationId = pageTracking.locationId ? pageTracking.locationId : null;
        // dataLayer.push accept only anonymous object
        this.dataLayer.push(JSON.parse(JSON.stringify(localPageTracking)));
      } catch (err) {
        this.logger.logError(err);
      }
    }
  }

  public sendPageName(pageName: string): void {
    const gtmPageTracking = new PageTrackingGtmDto();
    gtmPageTracking.pageName = pageName;
    this.sendPageLoadEvent(gtmPageTracking);
  }

  /**
   * This event will trigger when user select a location
   * @param locationTracking
   */
  sendLocationSelectEvent(locationTracking: BaseGTMDTO) {
    if (locationTracking) {
      try {
        const localLocationTracking = new BaseGTMDTO();
        localLocationTracking.event = locationTracking.event ? locationTracking.event : GtmExternal.EVENT_LOCATIONSELECTED;
        localLocationTracking.userId = (!this.isUserLoggedIn) ? null : this.user.id;
        localLocationTracking.ecommerce = locationTracking.ecommerce;
        localLocationTracking.locationId = locationTracking.locationId ? locationTracking.locationId : null;
        // dataLayer.push accept only anonymous object
        this.dataLayer.push(JSON.parse(JSON.stringify(localLocationTracking)));
      } catch (err) {
        this.logger.logError(err);
      }
    }
  }
  /**
   * Send event on place order
   * @param placeOrder
   */
  sendPlaceOrderEvent(placeOrder: BaseGTMDTO) {
    if (placeOrder) {
      try {
          const localPlaceOrder = new BaseGTMDTO();
          localPlaceOrder.event = placeOrder.event ? placeOrder.event : GtmExternal.EVENT_PLACEORDER;
          localPlaceOrder.userId = (!this.isLoggedIn) ? null : this.user.id;
          localPlaceOrder.ecommerce = placeOrder.ecommerce;
          localPlaceOrder.latitude = localPlaceOrder.latitude ? localPlaceOrder.latitude :
              ((this.locTrackingService.getLatitude()) ? this.locTrackingService.getLatitude() : 'null');
          localPlaceOrder.longitude = localPlaceOrder.longitude ? localPlaceOrder.longitude :
              ((this.locTrackingService.getLongitude()) ? this.locTrackingService.getLongitude() : 'null');
          localPlaceOrder.locationId = placeOrder.locationId ? placeOrder.locationId : null;
          if (placeOrder.payment_type) {
            localPlaceOrder.payment_type = placeOrder.payment_type;
          }
          if (placeOrder.payment_system) {
            localPlaceOrder.payment_system = placeOrder.payment_system;
          }
          this.roundLatLong(localPlaceOrder);
          // dataLayer.push accept only anonymous object
          this.dataLayer.push(JSON.parse(JSON.stringify(localPlaceOrder)));
        } catch (err) {
          this.logger.logError(err);
        }
    }
  }

  /**
   * Send event on checkout
   * @param placeOrder
   */
  sendCheckOutEvent(checkOut: BaseGTMDTO) {
    if (checkOut) {
      try {
        const localCheckOut = new BaseGTMDTO();
        localCheckOut.event = checkOut.event ? checkOut.event : GtmExternal.EVENT_CHECKOUT;
        localCheckOut.userId = (!this.isLoggedIn) ? null : this.user.id;
        localCheckOut.ecommerce = checkOut.ecommerce;
        localCheckOut.latitude = checkOut.latitude ? checkOut.latitude :
            ((this.locTrackingService.getLatitude()) ? this.locTrackingService.getLatitude() : null);
        localCheckOut.longitude = checkOut.longitude ? checkOut.longitude :
            ((this.locTrackingService.getLongitude()) ? this.locTrackingService.getLongitude() : null);
        localCheckOut.locationId = checkOut.locationId ? checkOut.locationId : null;
        this.roundLatLong(localCheckOut);
        // dataLayer.push accept only anonymous object
        this.dataLayer.push(JSON.parse(JSON.stringify(localCheckOut)));
      } catch (err) {
          this.logger.logError(err);
      }
    }
  }

  private roundLatLong(ecommerce: BaseGTMDTO) {
    if (ecommerce.latitude) {
      ecommerce.latitude = parseFloat(ecommerce.latitude).toFixed(2);
    }
    if (ecommerce.longitude) {
      ecommerce.longitude = parseFloat(ecommerce.longitude).toFixed(2);
    }
  }

  sendFavoriteEvent(gtmDto: FavoriteGTMDto) {
    if (gtmDto && gtmDto.status) {
      try {
        const localCheckOut = new FavoriteGTMDto();
        localCheckOut.event = GtmExternal.EVENT_FAVORITE;
        localCheckOut.userId = (!this.isLoggedIn) ? null : this.user.id;
        localCheckOut.ecommerce = gtmDto.ecommerce;
        localCheckOut.latitude = gtmDto.latitude ? gtmDto.latitude :
            ((this.locTrackingService.getLatitude()) ? this.locTrackingService.getLatitude() : null);
        localCheckOut.longitude = gtmDto.longitude ? gtmDto.longitude :
            ((this.locTrackingService.getLongitude()) ? this.locTrackingService.getLongitude() : null);
        localCheckOut.locationId = gtmDto.locationId ? gtmDto.locationId : null;
        localCheckOut.status = gtmDto.status;
        this.roundLatLong(localCheckOut);
        // dataLayer.push accept only anonymous object
        this.dataLayer.push(JSON.parse(JSON.stringify(localCheckOut)));
      } catch (err) {
          this.logger.logError(err);
      }
    }
  }

  sendModalEvent(modalTracking: ModalTrackingGtmDto) {
    if (modalTracking) {
      try {
        const localModalTracking = new ModalTrackingGtmDto();
        localModalTracking.event = modalTracking.event ? modalTracking.event : GtmExternal.EVENT_CONTENTCHANGE;
        localModalTracking.pagePath = modalTracking.pagePath ? modalTracking.pagePath : this.getWindowsObject().location.pathname;
        localModalTracking.pageUrl =  modalTracking.pageUrl ? modalTracking.pageUrl :
            'https:' + this.getWindowsObject().location.href.substring(
        this.getWindowsObject().location.protocol.length);
        localModalTracking.pageName = modalTracking.pageName;
        localModalTracking.userId = (!this.isUserLoggedIn) ? null : this.user.id;
        localModalTracking.modal = modalTracking.modal ? modalTracking.modal : null;
        localModalTracking.clickText = modalTracking.clickText ? modalTracking.clickText : null;
        // dataLayer.push accept only anonymous object
        this.dataLayer.push(JSON.parse(JSON.stringify(localModalTracking)));
      } catch (err) {
        this.logger.logError(err);
      }
    }
  }

  sendEvent(eventName: GtmExternal) {
    const base = new BaseGTMDTO();
    base.event = eventName.toString();
    base.ecommerce = null;
    this.dataLayer.push(JSON.parse(JSON.stringify(base)));
  }

  sendDoubleVisitModalEvent (modalTracking: VisitNudgeDto) {
    if (modalTracking) {
      try {
        const localModalTracking = new VisitNudgeDto();
        localModalTracking.event = modalTracking.event ? modalTracking.event : GtmExternal.EVENT_CHECKOUT_OFFER;
        localModalTracking.modal = modalTracking.modal ? modalTracking.modal : null;
        localModalTracking.current_card_value = modalTracking.current_card_value;
        localModalTracking.screenName = GtmExternal.VALUE_MYORDER;
        // dataLayer.push accepts only anonymous object
        this.dataLayer.push(JSON.parse(JSON.stringify(localModalTracking)));
      } catch (err) {
        this.logger.logError(err);
      }
    }
  }

  sendGenericEvent(eventPayload: any) {
    if (eventPayload) {
      try {
        this.dataLayer.push(JSON.parse(JSON.stringify(eventPayload)));
      } catch (error) {
        this.logger.logError(error);
      }
    }
  }
  getGTMCartPayloadFromRecipe(c: ChildRecipeEntity, method: string = null, index: number = 1): CartGTM {
    if (!method || method !== 'remove_from_cart') {
      method = sessionStorage.getItem('gtmOrderType') ?? 'cart';
    }
    
    const event: CartGTM = {
      event: method === 'remove_from_cart' ? 'remove_from_cart' : 'add_to_cart'      
    };
    if(method !== 'remove_from_cart') {
      event.add_method = method
    }

    const eCommerceItemArray: ECommerceItemArray = { items: [] };
    const recipeId = c.recipeId ? c.recipeId : c.id;
    const item: Item = {
      item_name: this.removeNonASCII(c?.recipeBasicInfo?.parentRecipeName),
      item_id: c.parentRecipeId?.toString(),
      item_category: this.removeNonASCII(c?.categoryName),
      item_category2: this.removeNonASCII(c?.subTitle),
      item_variant: ReciptTypeNames[c.recipeTypeId],
      item_category3: recipeId?.toString(),
      // index: 1, // Revisit this
      price: c.totalPrice,
      quantity: c.quantity ? c.quantity : 1
    }
    eCommerceItemArray.items.push(item);
    event.ecommerce = eCommerceItemArray;
    return event;

  }  
  getGTMEventPayloadFromRecipe(c: ChildRecipeEntity, method: string = null, index: number = 1): CartGTM {  
    
    const event: CartGTM = {
      event: method     
    };    
    if(method == 'select_item') {
      event.item_list_name = c?.categoryName;
    }

    const eCommerceItemArray: ECommerceItemArray = { items: [] };
    const item: Item = this.getGTMItemFromRecipe(c);
    eCommerceItemArray.items.push(item);
    event.ecommerce = eCommerceItemArray;
    return event;

  }
  private removeNonASCII(s: string) {
    return s?.replace(/[^\x00-\x7F]/g, "");
  }
  private getGTMItemFromRecipe(c: ChildRecipeEntity): Item {
    const recipeId = c.recipeId ? c.recipeId : c.id;
    return {
      item_name: this.removeNonASCII(c?.recipeBasicInfo?.parentRecipeName),
      item_id: c.parentRecipeId?.toString(),
      item_category: this.removeNonASCII(c?.categoryName),
      item_category2: this.removeNonASCII(c?.subTitle),
      item_variant: ReciptTypeNames[c.recipeTypeId],
      item_category3: recipeId?.toString(),
      index: 1, // Revisit this
      price: c.totalPrice,
      quantity: c.quantity ? c.quantity : 1
    };
  }

  getGTMCartPayloadFromCart(o: Order, eventName: string): CartGTM {
    const event: CartGTM = {
      event: eventName     
    };
    const eCommerceItemArray: ECommerceItemArray = { items: [] };
    o.childRecipes.forEach(c => {
      const item: Item = this.getGTMItemFromRecipe(c);
      eCommerceItemArray.items.push(item);
    });
    event.ecommerce = eCommerceItemArray;
    return event;
  }
  getPurchaseEventFromOrder(oc: Order, locationsEntity: LocationsEntity, orderSummaryItems: OrderSummaryItems, pickupMethod, orderNumber) {

    const isCustomized: boolean = 
                              _.flatMap(oc.childRecipes, c => 
                                _.flatMap(c.customizationGroups, cg => 
                                  cg.modifierGroups.map(mg => ({modifierGroup: mg, custGroup: cg}))
                                )
                              ).some(({modifierGroup, custGroup}) => modifierGroup.isSelected != isModifierGroupDefaultSelected(modifierGroup, custGroup)) || 
                            _.flatMap(_.flatMap(_.flatMap(
                                              oc.childRecipes, c=> c.customizationGroups), 
                                                mg=> mg.modifierGroups), 
                                                  m => m.modifiers).some(r => r && r.isSelected!== undefined  && r.isSelected != r.isDefaultSelected); 


    let couponCode = oc.childRecipes.find(c => c.offerCouponCode)?.offerCouponCode?.toString();    
    couponCode = couponCode ? couponCode.toString() : '';
    
    const event: OrderGTM = {
      event: 'purchase',
      ecommerce: {
        affiliation:"Online Store",
        currency:"USD",
        is_customized: isCustomized,
        locationid: locationsEntity.unit.toString(),
        pickup_method: PickupTypeDisplayValue[PickupType[pickupMethod]],
        coupon: couponCode,
        transaction_id: orderNumber,
        order_method: PickupTypeDisplayValue[PickupType[pickupMethod]],
        shipping: orderSummaryItems.deliveryFee ? orderSummaryItems.deliveryFee.toString() : '0',
        tax: orderSummaryItems.tax ? orderSummaryItems.tax.toString() : '0',
        value: orderSummaryItems.orderTotal ? orderSummaryItems.orderTotal.toString() : '0'
      }
    };
    const items = this.getGTMItems(oc, null);    
    event.ecommerce.items = items;
    return event;
  }
  getPaymentInfoFromOrder(oc: Order, locationsEntity: LocationsEntity, orderSummaryItems: OrderSummaryItems) {
  
    let couponCode = oc.childRecipes.find(c => c.offerCouponCode)?.offerCouponCode?.toString();    
    couponCode = couponCode ? couponCode.toString() : '';
    
    const event: OrderGTM = {
      event: 'add_payment_info',
      ecommerce: {        
        currency:'USD',        
        coupon: couponCode,
        value: orderSummaryItems.orderTotal ? orderSummaryItems.orderTotal.toString() : '0',
        payment_type: 'Credit Card'
      }
    };
    const items = this.getGTMItems(oc, locationsEntity);      
    event.ecommerce.items = items;
    return event;
  }
  private getGTMItems(oc: Order, locationsEntity: LocationsEntity) {
    const items: Item[] = [];
    oc.childRecipes.forEach(c => {
      const categoryName = this.removeNonASCII(c?.categoryName);
      const recipeId = c.recipeId ? c.recipeId : c.id;
      const item: Item = {
        item_name: this.removeNonASCII(c?.recipeBasicInfo.parentRecipeName),
        item_id: c.parentRecipeId?.toString(),
        item_category: categoryName,
        item_category2: this.removeNonASCII(c?.subTitle),
        item_variant: ReciptTypeNames[c.recipeTypeId],
        item_category3: recipeId?.toString(),
        price: c.totalPrice,
        quantity: c.quantity ? c.quantity : 1,
        item_list_name: categoryName
      };
      if(locationsEntity) {
        item.locationid = locationsEntity.unit.toString();
      }
      items.push(item);
    });
    return items;
  }
  getGTMEventPayloadFromRecipes(recipes: ChildRecipeEntity[], method: string = null, listName: string = null): CartGTM {  
    
    const event: CartGTM = {
      event: method,          
    };    
    if(method == 'select_item') {
      event.item_list_name = listName;
    }

    const eCommerceItemArray: ECommerceItemArray = { items: [] };
    recipes.forEach(c => {
      const item: Item = this.getGTMItemFromRecipe(c);
      eCommerceItemArray.items.push(item);
    });    
    event.ecommerce = eCommerceItemArray;
    return event;
  }
}
